// Справка „Отстъпки и надбавки" (Справки → discounts).
//
// Един въпрос: колко е раздадено, от кого и по кои сметки. Затова видът на
// корекцията НЕ е акцент — „Вид" и „Сервитьор" са обикновени колони с филтър
// в самата колона, а не селекти над таблицата, както беше досега.
//
// `GET /reports/analytics/discounts` връща целия период наведнъж (масив, без
// страниране), затова таблицата работи в КЛИЕНТСКИ режим: сортирането,
// филтрите и редът „Общо" се смятат в браузъра, а Excel изнася видяното.

import { useMemo, useState } from "react";
import { App, Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useBillsHistory, useDiscountsReport } from "../../../hooks/useReports";
import { useAppSelector } from "../../../store/hooks";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable, parseDecimal } from "../../../components/dataTable";
import { getBillHistoryById } from "../../../api/reportsBillsHistory";
import type {
  BillHistory,
  BillsHistoryFilters,
} from "../../../api/reportsBillsHistory";
import ExportExcelButton from "./ExportExcelButton";
import BillOperationsModal from "./BillOperationsModal";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { pluralLabel } from "./reportPlural";
import { DiscountsKpis } from "./discounts/DiscountsKpis";
import { DiscountsMobileCard } from "./discounts/DiscountsMobileCard";
import { useDiscountsColumns } from "./discounts/discountsColumns";
import { exportDiscounts } from "./discounts/discountsExport";
import {
  APPROVAL_PERCENT,
  buildDiscountRows,
  summarizeDiscounts,
} from "./discounts/discountsMath";
import type { NamedDiscountRow } from "./discounts/discountsMath";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

/**
 * Правото, което пуска до общия брой сметки и оборота за периода — знаменателят
 * на „X % от всички" и „X % от брутото".
 *
 * Това е правото на САМАТА справка, не на „Справка сметки": `/bills/history` с
 * `context="reports"` дава всички сметки на всеки, който има което и да е от
 * правата на справките (виж `bills/router.py`). Гейт по `view_tables_history`
 * криеше двата дяла точно от човека, който отваря този екран.
 */
const ALL_BILLS_PERMISSION = "view_discounts_report";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const DiscountsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const { message } = App.useApp();

  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isolatedAboveApproval, setIsolatedAboveApproval] = useState(false);
  const [selectedBill, setSelectedBill] = useState<BillHistory | null>(null);
  // Редовете СЛЕД филтър и подредба — Excel изнася видяното, не суровия набор.
  const [visibleRows, setVisibleRows] = useState<NamedDiscountRow[]>([]);

  const permissions = useAppSelector((state) => state.auth.permissions);
  const canSeeAllBills = permissions.some(
    (permission) => permission.name === ALL_BILLS_PERMISSION,
  );

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
      end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  // Предходният равен по дължина период — само за Δ чиповете. Тече ли още
  // избраният период, краят се подрязва до изтеклото (виж `reportPeriod`),
  // иначе непълен ден стои срещу пълен.
  const previousFilters = useMemo(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    const previous = previousPeriodOf(dateRange[0], dateRange[1]);
    return {
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange]);

  const {
    data: items = [],
    isLoading,
    isFetching,
    refetch,
  } = useDiscountsReport(filters);
  // При изключено сравнение се пита за СЪЩИЯ период — React Query го дава от
  // кеша, без втора мрежова заявка, а резултатът просто не се ползва.
  const { data: previousItems = [] } = useDiscountsReport(
    previousFilters ?? filters,
  );

  // Двата дяла в кутийките („от всички сметки", „от брутото") искат числа,
  // които тази справка не получава. Взимат се от обобщението на „Справка
  // сметки" — със `summary_only`, за да не се тегли и списъкът — и само ако
  // правото го пуска: иначе заявката би върнала 403 при всяко отваряне.
  const allBillsFilters = useMemo<BillsHistoryFilters>(
    () => ({
      page: 1,
      page_size: 1,
      summary_only: true,
      context: "reports",
      date_field: "closed_at",
      statuses: ["closed"],
      ...filters,
    }),
    [filters],
  );
  const { data: allBills } = useBillsHistory(allBillsFilters, {
    enabled: canSeeAllBills,
  });

  const summary = useMemo(() => summarizeDiscounts(items), [items]);
  const previousSummary = useMemo(
    () => (previousFilters ? summarizeDiscounts(previousItems) : null),
    [previousFilters, previousItems],
  );

  const discountTypeLabel = useMemo(() => {
    const byType: Record<string, string> = {
      fixed_percent: t("reportsPage.discountTypeFixedPercent"),
      cashback: t("reportsPage.discountTypeCashback"),
      cash_back: t("reportsPage.discountTypeCashback"),
    };
    // Непознат вид се показва както идва от BE — по-полезно от празна клетка,
    // когато утре се появи трети вид отстъпка.
    return (type: string | null) => (type ? (byType[type] ?? type) : "");
  }, [t]);

  // Етикетите влизат в САМИТЕ редове: `accessorFn`, който вика `t(...)`, се
  // изчислява веднъж и заковава стойността за живота на `data` — смяна на
  // езика не би стигнала до клетката.
  const namedRows = useMemo(
    () =>
      buildDiscountRows(items, {
        discount: t("reportsPage.discountTag"),
        surcharge: t("reportsPage.surchargeTag"),
        discountType: discountTypeLabel,
      }),
    [items, t, discountTypeLabel],
  );

  // Търсачката гледа това, което човек чете в реда: сметка, сервитьор, клиент.
  // Изолирането на едрите отстъпки идва от кутийката „Над 30 %".
  const tableRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return namedRows.filter((row) => {
      if (
        isolatedAboveApproval &&
        !(row.kind === "discount" && row.percentAbs > APPROVAL_PERCENT)
      ) {
        return false;
      }
      if (!needle) return true;
      return [row.billLabel, row.waiterLabel, row.clientLabel]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [namedRows, searchText, isolatedAboveApproval]);

  const isMultiDay = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return false;
    return !dateRange[0].isSame(dateRange[1], "day");
  }, [dateRange]);

  const columns = useDiscountsColumns(isMultiDay);

  const openBill = async (billId: number) => {
    try {
      const bill = await getBillHistoryById(billId, "reports");
      // Липсваща сметка е същият отказ както грешка по мрежата — тихото
      // „нищо не се случи" оставя човека да натиска реда пак и пак.
      if (bill) setSelectedBill(bill);
      else message.error(t("reportsPage.discountsBillOpenFailed"));
    } catch {
      message.error(t("reportsPage.discountsBillOpenFailed"));
    }
  };

  const shownCount = visibleRows.length;

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.clientsTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <DiscountsKpis
                summary={summary}
                previous={previousSummary}
                closedBills={
                  canSeeAllBills
                    ? (allBills?.summary.closed_count ?? null)
                    : null
                }
                periodRevenue={
                  canSeeAllBills && allBills
                    ? parseDecimal(allBills.summary.total_revenue)
                    : null
                }
                isolatedAboveApproval={isolatedAboveApproval}
                onToggleAboveApproval={() =>
                  setIsolatedAboveApproval((previous) => !previous)
                }
                isNarrow={narrow}
              />

              <DataTable<NamedDiscountRow>
                tableId="discounts"
                data={tableRows}
                columns={columns}
                getRowId={(row) => row.rowId}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.clientsTitle")}
                title={
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                      {t("reportsPage.discountsListKicker")}
                    </span>
                    <span className="text-[16px] font-extrabold leading-none text-ink">
                      {pluralLabel(
                        t,
                        shownCount,
                        "reportsPage.discountsResultOne",
                        "reportsPage.discountsResults",
                      )}
                      <span className="font-medium text-muted">
                        {` · ${t("reportsPage.discountsTableHint")}`}
                      </span>
                    </span>
                  </div>
                }
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.discountsSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportDiscounts(visibleRows, { t })}
                    disabled={shownCount === 0}
                    bare
                  />
                }
                onRowClick={(row) => openBill(row.bill_id)}
                mobileCard={(row) => <DiscountsMobileCard row={row} />}
                emptyTitle={t("reportsPage.discountsNoData")}
                emptyHint={t("reportsPage.discountsEmptyHint")}
                initialSorting={[{ id: "closed_at", desc: true }]}
              />
            </>
          )}
        </div>
      </div>

      {selectedBill ? (
        <BillOperationsModal
          bill={selectedBill}
          open
          onClose={() => setSelectedBill(null)}
          onBillUpdated={() => refetch()}
          // БЕЗ `showCancelButton`: тази справка е за четене.
        />
      ) : null}
    </div>
  );
};
